import { createContext, useContext, useLayoutEffect, useState, type RefObject } from 'react';

export type FullscreenBoundaryContextValue = {
    contentWrapperRef: React.RefObject<HTMLDivElement | null>;
};

export type EditorPaneBounds = {
    top: number;
    left: number;
    width: number;
    height: number;
};

export const FullscreenBoundaryContext = createContext<FullscreenBoundaryContextValue>({
    contentWrapperRef: { current: null },
});

export const useFullscreenBoundaryContext = (): FullscreenBoundaryContextValue => {
    return useContext(FullscreenBoundaryContext);
};

function readBounds(el: HTMLElement): EditorPaneBounds {
    const rect = el.getBoundingClientRect();
    return {
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
    };
}

export function useEditorPaneBounds(boundaryRef: RefObject<HTMLElement | null>, active: boolean): EditorPaneBounds | null {
    const [bounds, setBounds] = useState<EditorPaneBounds | null>(null);
    useLayoutEffect(() => {
        if (!active) return;

        const el = boundaryRef.current;
        if (!el) return;

        const update = () => setBounds(readBounds(el));
        update();

        window.addEventListener('resize', update);
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => {
            window.removeEventListener('resize', update);
            ro.disconnect();
        };
    }, [active, boundaryRef]);
    return active ? bounds : null;
}
